<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :closable="true"
    :draggable="false"
    class="subscription-dialog"
    :style="{ width: '90vw', maxWidth: '450px' }">
    <div class="dialog-content">
      <i v-if="isMainUser" class="pi pi-exclamation-triangle text-orange-500" style="font-size: 3rem"></i>
      <i v-else class="pi pi-lock text-red-500" style="font-size: 3rem"></i>

      <h3 class="dialog-title">
        {{ isMainUser ? $t('subscription.configureNewPlan') : $t('subscription.noSubscriptionTitle') }}
      </h3>

      <p class="dialog-message">
        {{ isMainUser ? $t('subscription.mainUserNoPlanMessage') : $t('subscription.nonMainUserAlert') }}
      </p>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <Button
          v-if="isMainUser"
          :label="$t('subscription.viewPlans')"
          icon="pi pi-shopping-cart"
          class="p-button-primary"
          @click="goToPlans"
          autofocus />
        <Button
          :label="isMainUser ? $t('common.later') : $t('common.understood')"
          icon="pi pi-check"
          :class="isMainUser ? 'p-button-text' : 'p-button-primary'"
          @click="closeDialog" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { useStates } from '../router/states';

const { userState } = useStates();
const router = useRouter();
const dialogVisible = ref(false);

const STORAGE_KEY = 'subscription_alert_shown';

// Reset para testing - comentar en producción
sessionStorage.removeItem(STORAGE_KEY);

const isMainUser = computed(() => {
  const userData = userState?.data?.value;
  return userData?.isMainUser === true;
});

// Verificar si se debe mostrar el diálogo
const checkAndShowDialog = () => {
  const userData = userState?.data?.value;

  if (!userData) {
    return;
  }

  // Verificar permisos del token
  const token = sessionStorage.getItem('authToken');
  if (token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      const payload = JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  // Mostrar si NO tiene suscripción activa (tanto para main como no-main)
  // Considerar null/undefined como false también
  const subscriptionActive = userData.subscriptionActive;
  const noSubscription =
    subscriptionActive === false || subscriptionActive === null || subscriptionActive === undefined;
  const shouldShow = noSubscription;

  if (shouldShow) {
    // Verificar si ya se mostró en esta sesión
    const alreadyShown = sessionStorage.getItem(STORAGE_KEY);

    if (!alreadyShown) {
      dialogVisible.value = true;
    }
  }
};

const goToPlans = () => {
  dialogVisible.value = false;
  sessionStorage.setItem(STORAGE_KEY, 'true');
  router.push('/subscription-plans');
};

const closeDialog = () => {
  dialogVisible.value = false;
  sessionStorage.setItem(STORAGE_KEY, 'true');
};

// Observar cambios en el estado del usuario
watch(
  () => userState?.data?.value,
  (newValue) => {
    if (newValue) {
      checkAndShowDialog();
    }
  },
  { immediate: true },
);

onMounted(() => {
  checkAndShowDialog();
});
</script>

<style scoped>
.subscription-dialog :deep(.p-dialog-content) {
  padding: 2rem 1.5rem;
}

.subscription-dialog :deep(.p-dialog-footer) {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: center;
}

.dialog-content {
  text-align: center;
}

.dialog-title {
  font-weight: bold;
  font-size: 1.25rem;
  color: #2d3436;
  margin: 0 0 1rem 0;
}

.dialog-message {
  font-size: 1rem;
  color: #636e72;
  line-height: 1.6;
  margin: 0;
}
</style>
